import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { uploadToS3, getFilesFromS3, deleteFromS3 } from '../../services/s3Service';
import { addMediaRecord, getMediaRecords, deleteMediaRecord } from '../../services/firebaseService';

const initialState = {
  images: [],
  videos: [],
  allMedia: [],
  s3Media: [], // Media from S3 bucket
  uploadProgress: 0,
  uploading: false,
  loading: false,
  loadingS3: false,
  error: null,
  filter: 'all', // all, image, video
};

// Async thunks
export const fetchMedia = createAsyncThunk(
  'media/fetchMedia',
  async (userId, { rejectWithValue }) => {
    try {
      return new Promise((resolve, reject) => {
        getMediaRecords(userId, (media) => {
          resolve(media);
        }, reject);
      });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const uploadMedia = createAsyncThunk(
  'media/uploadMedia',
  async ({ userId, file, type, storeId }, { rejectWithValue, dispatch }) => {
    try {
      if (!storeId) {
        throw new Error('Store ID is required for upload');
      }

      dispatch(setUploading(true));
      dispatch(setUploadProgress(0));

      // Use the path: slideconfig/clientmedia/${storeId}
      const folderPath = `slideconfig/clientmedia/${storeId}`;
      const s3Url = await uploadToS3(file, folderPath);

      dispatch(setUploadProgress(100));
      dispatch(setUploading(false));

      // Return media data (will be fetched from S3, no need to save to Firebase)
      return {
        id: `s3-${folderPath}/${file.name}`,
        name: file.name,
        url: s3Url,
        type,
        size: file.size,
        uploadedAt: new Date().toISOString(),
        source: 's3',
      };
    } catch (error) {
      dispatch(setUploading(false));
      return rejectWithValue(error.message);
    }
  }
);

export const removeMedia = createAsyncThunk(
  'media/removeMedia',
  async ({ userId, mediaId, s3Key }, { rejectWithValue, dispatch }) => {
    try {
      // Delete from S3
      if (s3Key) {
        await deleteFromS3(s3Key);
      }

      // Delete from Firebase (if it exists)
      if (mediaId && !mediaId.startsWith('s3-')) {
        await deleteMediaRecord(userId, mediaId);
      }

      // Refresh S3 media after deletion to update the list
      // Get storeId from s3Key if available
      if (s3Key) {
        const storeIdMatch = s3Key.match(/slideconfig\/clientmedia\/([^/]+)/);
        if (storeIdMatch && storeIdMatch[1]) {
          dispatch(fetchS3Media(storeIdMatch[1]));
        }
      }

      return mediaId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Fetch media from S3 using cloud function
 * @param {string} storeId - Store ID to fetch media for
 */
export const fetchS3Media = createAsyncThunk(
  'media/fetchS3Media',
  async (storeId, { rejectWithValue }) => {
    try {
      if (!storeId) {
        return [];
      }

      const s3Path = `slideconfig/clientmedia/${storeId}/`;
      const API_BASE = process.env.REACT_APP_FIREBASE_FUNCTIONS_URL || 'https://us-central1-digislidesapp.cloudfunctions.net';
      
      const response = await fetch(`${API_BASE}/s3ListFiles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          folderPath: s3Path,
          maxKeys: 1000,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch S3 media: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Transform S3 files to media format
      const mediaFiles = (data.files || [])
        .filter((file) => {
          const fileName = file.key.split('/').pop();
          const fileExtension = fileName.split('.').pop()?.toLowerCase();
          return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'mp4', 'webm', 'ogg', 'mov'].includes(fileExtension);
        })
        .map((file) => {
          const fileName = file.key.split('/').pop();
          const fileExtension = fileName.split('.').pop()?.toLowerCase();
          const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(fileExtension);
          
          return {
            id: `s3-${file.key}`,
            name: fileName,
            url: file.url,
            type: isImage ? 'image' : 'video',
            size: file.size || 0,
            uploadedAt: file.lastModified || new Date().toISOString(),
            s3Key: file.key,
            source: 's3', // Mark as S3 media
          };
        });

      return mediaFiles;
    } catch (error) {
      console.error('Error fetching S3 media:', error);
      return rejectWithValue(error.message);
    }
  }
);

const mediaSlice = createSlice({
  name: 'media',
  initialState,
  reducers: {
    setFilter: (state, action) => {
      state.filter = action.payload;
    },
    setUploading: (state, action) => {
      state.uploading = action.payload;
    },
    setUploadProgress: (state, action) => {
      state.uploadProgress = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Media
      .addCase(fetchMedia.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMedia.fulfilled, (state, action) => {
        state.loading = false;
        state.allMedia = action.payload;
        state.images = action.payload.filter((m) => m.type === 'image');
        state.videos = action.payload.filter((m) => m.type === 'video');
      })
      .addCase(fetchMedia.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Upload Media
      .addCase(uploadMedia.fulfilled, (state, action) => {
        // Don't add to allMedia - it will be fetched from S3
        // This prevents duplicates when combining allMedia and s3Media
        state.uploadProgress = 0;
      })
      .addCase(uploadMedia.rejected, (state, action) => {
        state.error = action.payload;
        state.uploadProgress = 0;
      })
      // Remove Media
      .addCase(removeMedia.fulfilled, (state, action) => {
        const mediaId = action.payload;
        // Remove from allMedia (Firebase media)
        state.allMedia = state.allMedia.filter((m) => m.id !== mediaId);
        state.images = state.images.filter((m) => m.id !== mediaId);
        state.videos = state.videos.filter((m) => m.id !== mediaId);
        // Remove from s3Media
        state.s3Media = state.s3Media.filter((m) => m.id !== mediaId);
      })
      // Fetch S3 Media
      .addCase(fetchS3Media.pending, (state) => {
        state.loadingS3 = true;
        state.error = null;
      })
      .addCase(fetchS3Media.fulfilled, (state, action) => {
        state.loadingS3 = false;
        state.s3Media = action.payload;
      })
      .addCase(fetchS3Media.rejected, (state, action) => {
        state.loadingS3 = false;
        state.error = action.payload;
        state.s3Media = [];
      });
  },
});

export const { setFilter, setUploading, setUploadProgress, clearError } = mediaSlice.actions;

export default mediaSlice.reducer;

